import { Box, Grow, Typography, useTheme } from '@mui/material';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { importModalAtom } from '@/atoms/import';
import { CloseSvg } from '@/components/Icons/Close.Svg';
import { ImportInfoBody, ImportInfoHead, ImportInfoWrap } from './ImportScript.styles';
import { ImportScriptCharacters } from './ImportScriptCharacters';
import { ImportScriptStatistics } from './ImportScriptStatistics';
import { useRulesContent } from './useRulesContent';

const InfoTitle = ({ text, onClose }: { text: string; onClose: () => void }) => {
    return (
        <ImportInfoHead>
            <Typography
                children={text}
                sx={theme => ({
                    fontSize: '2.25rem',
                    fontWeight: '600',
                    lineHeight: '1.2',
                    color: theme.custom.c49,
                })}
            />
            <Box onClick={onClose} color={'info.contrastText'} sx={{ cursor: 'pointer', display: 'flex' }}>
                <CloseSvg fontSize="small" />
            </Box>
        </ImportInfoHead>
    );
};

export const ImportScriptInfo = () => {
    const { t } = useTranslation();
    const rulesContent = useRulesContent();
    const theme = useTheme();
    const [importModal, setImportModal] = useAtom(importModalAtom);
    const { showRules, showStatistics, showCharacters, statistics } = importModal;

    return (
        <ImportInfoWrap>
            <Grow in={showStatistics} unmountOnExit>
                <ImportInfoBody>
                    <InfoTitle text={t('import.statistics')} onClose={() => setImportModal(prev => ({ ...prev, showStatistics: false }))} />
                    {statistics ? <ImportScriptStatistics {...statistics} /> : null}
                </ImportInfoBody>
            </Grow>

            <Grow in={showCharacters} unmountOnExit>
                <ImportInfoBody>
                    <InfoTitle
                        text={t('character.characters')}
                        onClose={() =>
                            setImportModal(prev => ({
                                ...prev,
                                showCharacters: false,
                                showRules: false,
                                showStatistics: true,
                            }))
                        }
                    />
                    {statistics ? <ImportScriptCharacters characters={statistics.statistics.characters} /> : null}
                </ImportInfoBody>
            </Grow>

            <Grow in={showRules} unmountOnExit>
                <ImportInfoBody>
                    <InfoTitle
                        text={t('import.features')}
                        onClose={() =>
                            setImportModal(prev => ({
                                ...prev,
                                showRules: false,
                                showCharacters: false,
                                showStatistics: true,
                            }))
                        }
                    />
                    <ReactMarkdown
                        children={rulesContent}
                        components={{
                            ol: ({ children }) => (
                                <Typography
                                    variant="body1"
                                    component="ol"
                                    children={children}
                                    sx={{
                                        color: theme.custom.c42,
                                        display: 'grid',
                                        gap: '16px',
                                        marginBottom: '16px',
                                        paddingInlineStart: '24px',
                                    }}
                                />
                            ),
                            ul: ({ children }) => (
                                <Typography
                                    variant="body1"
                                    component="ul"
                                    children={children}
                                    sx={{
                                        color: theme.custom.c42,
                                        paddingInlineStart: '24px',
                                        listStyleType: 'disc',
                                    }}
                                />
                            ),
                            strong: ({ children }) => (
                                <Typography
                                    variant="body1"
                                    component="strong"
                                    children={children}
                                    sx={{
                                        display: 'inline-block',
                                        marginTop: '4px',
                                        marginBottom: '2px',
                                        fontWeight: 600,
                                        color: theme.custom.c47,
                                    }}
                                />
                            ),
                            p: ({ children }) => <Typography variant="body1" children={children} sx={{ color: theme.custom.c42 }} />,
                            h1: ({ children }) => (
                                <Typography
                                    component={'p'}
                                    sx={{
                                        marginBottom: '12px',
                                        fontSize: '1.375rem',
                                        fontWeight: 600,
                                        color: theme.custom.c3,
                                        '&:not(:first-of-type)': {
                                            marginTop: '16px',
                                        },
                                    }}
                                    children={children}
                                />
                            ),
                            blockquote: ({ children }) => (
                                <Typography
                                    component={'blockquote'}
                                    sx={{
                                        display: 'block',
                                        maxWidth: 'max-content',
                                        background: theme.palette.primary.main,
                                        borderRadius: '12px',
                                        padding: '8px 12px',
                                        '& + blockquote': {
                                            marginTop: '16px',
                                        },
                                        '.MuiTypography-root': {
                                            color: theme.custom.c42,
                                            fontWeight: 700,
                                            fontSize: '1.063rem',
                                            fontFamily: 'Courier New',
                                            em: {
                                                fontStyle: 'normal',
                                                color: theme.palette.success.main,
                                            },
                                            strong: {
                                                margin: 0,
                                                color: theme.palette.secondary.main,
                                            },
                                        },
                                        blockquote: {
                                            minWidth: '500px',
                                            '.MuiTypography-root': {
                                                maxWidth: '330px',
                                                width: '100%',
                                                display: 'block',
                                                margin: '0 auto',
                                                em: {
                                                    display: 'block',
                                                    color: 'inherit',
                                                    textAlign: 'center',
                                                },
                                            },
                                        },
                                    }}
                                    children={children}
                                />
                            ),
                        }}
                    />
                </ImportInfoBody>
            </Grow>
        </ImportInfoWrap>
    );
};
